/* eslint-disable */
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import React, { useState, useEffect } from 'react';
import * as R from 'ramda';
import styled from 'styled-components';

const BETWEEN_MODE = 'between';
const IN_MODE = 'in';

const buildValue = (value, nextValue, operators) => {
  if (!operators || operators.length === 0) {
    return nextValue.value;
  }

  let nextState = {
    operator: operators[0],
    value: '',
    ...value,
    ...nextValue,
  };

  nextState =
    nextState.operator === BETWEEN_MODE
      ? R.omit(['value'], nextState)
      : R.omit(['beginValue', 'endValue'], nextState);

  if (nextState.operator === IN_MODE && !Array.isArray(nextState.value)) {
    nextState.value = [nextState.value];
  }

  return nextState;
};

const TextInput = ({
  input,
  autoComplete = '',
  meta: { touched = false, error },
  label,
  needHightlight = false,
  multiline = false,
  operators = [],
  defaultOperator,
  style = {},
  type = 'text',
  numberMaskSetting,
  ...custom
}) => {
  const [blurredValue, setBlurredValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const value =
    operators.length !== 0
      ? R.pathOr('', ['value', 'value'], input)
      : input.value;
  const selectedOperator =
    R.path(['value', 'operator'], input) || defaultOperator;
  const isBetweenMode = selectedOperator === BETWEEN_MODE;

  const otherProps = {
    'data-ishightlightfromvalue': Boolean(needHightlight && value),
    'data-ishightlightfromprops': needHightlight,
  };

  useEffect(() => {
    if (numberMaskSetting) {
      let numberMaskValue = value;
      if (!isNaN(numberMaskValue)) {
        let options = {};
        if (!isNaN(numberMaskSetting.digits)) {
          options = {
            maximumFractionDigits: numberMaskSetting.digits,
            minimumFractionDigits: numberMaskSetting.digits,
          };
        }
        setBlurredValue(
          Number(numberMaskValue).toLocaleString('en-US', options),
        );
      }
    } else {
      setBlurredValue(value);
    }
  }, [value]);

  return (
    <Wrapper style={style} {...otherProps}>
      {!isBetweenMode ? (
        <TextField
          autoComplete={autoComplete}
          error={Boolean(touched && error)}
          label={label}
          multiline={multiline}
          type={type}
          value={isFocused ? value : blurredValue}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={() => {
            setIsFocused(false);
            input.onBlur(input.value);
          }}
          onChange={(event) => {
            if (type === 'number') {
              input.onChange(
                buildValue(
                  input.value,
                  { value: event.target.value },
                  operators,
                ),
                event,
              );
              return;
            }
            input.onChange(
              buildValue(input.value, { value: event.target.value }, operators),
              event,
            );
          }}
          {...custom}
        />
      ) : (
        <BetweenRow>
          <TextField
            autoComplete={autoComplete}
            label={label}
            multiline={multiline}
            type={type}
            value={R.pathOr('', ['value', 'beginValue'], input)}
            onBlur={() => input.onBlur(input.value)}
            onChange={(event) => {
              if (type === 'number') {
                input.onChange(
                  buildValue(
                    input.value,
                    { beginValue: event.target.value },
                    operators,
                  ),
                  event,
                );
                return;
              }

              input.onChange(
                buildValue(
                  input.value,
                  { beginValue: event.target.value },
                  operators,
                ),
                event,
              );
            }}
            {...custom}
          />
          <span style={{ margin: '0 10px' }}>To</span>
          <TextField
            autoComplete={autoComplete}
            multiline={multiline}
            type={type}
            value={R.pathOr('', ['value', 'endValue'], input)}
            onBlur={() => input.onBlur(input.value)}
            onChange={(event) => {
              if (type === 'number') {
                input.onChange(
                  buildValue(
                    input.value,
                    { endValue: event.target.value },
                    operators,
                  ),
                  event,
                );
                return;
              }

              input.onChange(
                buildValue(
                  input.value,
                  { endValue: event.target.value },
                  operators,
                ),
                event,
              );
            }}
          />
        </BetweenRow>
      )}

      {operators.length > 1 && (
        <OperatorsPanel>
          {operators.map((operator, ind) => (
            <Operator
              isActive={
                selectedOperator ? selectedOperator === operator : ind === 0
              }
              key={`${input.name}_${operator}`}
              onClick={() =>
                input.onChange(buildValue(input.value, { operator }, operators))
              }
            >
              {operator}
            </Operator>
          ))}
        </OperatorsPanel>
      )}
      {touched && error && <FormHelperText error>{error}</FormHelperText>}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  margin-bottom: 15px;

  > div {
    width: 100%;
  }
  .MuiInputBase-input {
    ${(props) =>
      props['data-ishightlightfromvalue'] &&
      `
      background-color: rgba(194, 217, 43, 0.13);
    `}
  }
  .Mui-focused {
    .MuiInputBase-input {
      ${(props) =>
        props['data-ishightlightfromprops'] &&
        `
        background-color: rgba(194, 217, 43, 0.13);
      `}
    }
  }
`;

const BetweenRow = styled.div`
  display: flex;
  align-items: flex-end;

  .MuiInputLabel-formControl {
    transform: translate(0, 1.5px) scale(0.75) !important;
  }
`;

const OperatorsPanel = styled.p`
  position: absolute;
  display: flex;
  align-items: center;
  top: 5px;
  right: 0;
`;

const Operator = styled.span`
  display: block;
  margin: 0 0 0 8px;
  color: ${(props) => (props.isActive ? '#0692CD' : '#333')};
  font-size: ${(props) => (props.isActive ? 14 : 13)}px;
  font-weight: ${(props) => (props.isActive ? 'bold' : 'normal')};
  opacity: ${(props) => (props.isActive ? 1 : 0.75)};
  cursor: pointer;
`;

export default TextInput;
